import {
    Row,
    Col,
    Typography,
    Table,
    Button,
    message,
    Descriptions,
  } from "antd";
  import { routes } from "common/routes";
  import { generateHeaders, getSlotName } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
  import { useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
  import { cancelFaPickingUserMapping, removeProcessingFaPickingUserMapping, saveFaPickingUserMapping } from "store/na-picking/naPickingSlice";
  
  const MESSAGE_KEY = "SAVE_USER_MAPPING";
  
  export const SaveUserMappingFAPickingPage = () => {
    useDocumentTitle("FA Picking: Save User Mapping");
    const { processingFaPickingUserMapping, status, error } = useAppSelector(
      (state) => state.naPicking
    );
    const { slots } = useAppSelector((state) => state.requisition);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
  
    useEffect(() => {
      const { timeUploaded } = processingFaPickingUserMapping;
      if (timeUploaded) {
        const uploadedTime = new Date(timeUploaded).getTime();
        const currentTime = new Date().getTime();
        const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
  
        if (minsPassed >= 15) {
          dispatch(removeProcessingFaPickingUserMapping());
        }
      }
  
      if (!Object.keys(processingFaPickingUserMapping).length) {
        navigate(`/${routes.SR_USER_MAPPING.FA_USER_MAPPING_UPLOAD}`);
      }
    }, [dispatch, processingFaPickingUserMapping, navigate]);
  
    useEffect(() => {
      if (status === "loading") {
        message.loading({ content: "saving user mapping", key: MESSAGE_KEY });
      } else if (status === "success") {
        message.success({
          content: "User mapping saved!",
          duration: 5,
          key: MESSAGE_KEY,
        });
      } else if (status === "cancelling") {
        message.loading({ content: "cancelling user mapping", key: MESSAGE_KEY });
      } else if (status === "error") {
        message.error({ content: error, key: MESSAGE_KEY });
      }
  
      return () => {
        message.destroy();
      };
    }, [status, error]);
  
    let content;
    const { headerMapping } = processingFaPickingUserMapping;
    const { Text } = Typography;
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMapping),
      [headerMapping]
    );
  
    const onSaveUserMapping = () => {
      dispatch(saveFaPickingUserMapping());
    };
  
    const onCancelSave = () => {
      dispatch(cancelFaPickingUserMapping());
    };
  
    if (!Object.keys(processingFaPickingUserMapping).length) {
      content = <Text>Nothing to show here!</Text>;
    } else {
      const headers = generateMemoizedHeaders || {};
      const successRows = processingFaPickingUserMapping.successRows;
      const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
      const columns = [
        ...Object.keys(headers).map(
          (key) =>
            ({
              key,
              title: headers[key],
              dataIndex: key,
            } as any)
        ),
      ];
      content = (
        <Table
          size="small"
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ y: 500 }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onSaveUserMapping}
                        loading={status === "loading"}
                        disabled={status === "loading"}
                      >
                        Save Mapping
                      </Button>
                    </Col>
                    <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                      <Button
                        onClick={onCancelSave}
                        disabled={status === "loading"}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        ></Table>
      );
    }
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Save FA Picking User Mapping" />
          </Col>
        </Row>
        <Row style={{ marginBottom: "1rem" }}>
          {!!(slots && processingFaPickingUserMapping.slot) && (
            <Descriptions
              size="small"
              layout="vertical"
              labelStyle={{ fontWeight: "bold" }}
              bordered
              column={{ xs: 2 }}
            >
              <Descriptions.Item label="Date">
                {processingFaPickingUserMapping.date}
              </Descriptions.Item>
              <Descriptions.Item label="Slot">
                {getSlotName(slots, processingFaPickingUserMapping.slot)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Row>
        <Row>
          <Col xs={24}>{content}</Col>
        </Row>
      </>
    );
  };
  